import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, CheckCheck, X, Sparkles, FileCheck2, Folder, CheckCircle, FileText, Info, AlarmClock,
  UserCheck, MessageSquare,
} from 'lucide-react';
import { losApi } from './losApi';
import TopNavPanel, { pressedOutside } from './components/TopNavPanel';
import { fromApi } from './kit/time';

export const ICON_MAP = {
  'sparkles': Sparkles,
  'file-check': FileCheck2,
  'folder': Folder,
  'check-circle': CheckCircle,
  'file': FileText,
  'alarm-clock': AlarmClock,
  'user-check': UserCheck,
  'message-square': MessageSquare,
};

const POLL_MS = 30000;

export function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.floor((Date.now() - fromApi(iso).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s/60)}m`;
  if (s < 86400) return `${Math.floor(s/3600)}h`;
  return `${Math.floor(s/86400)}d`;
}

// Optionally controlled (`open` + `onOpenChange`) so the top nav can keep
// one menu open at a time; uncontrolled when `open` is omitted.
export default function NotificationBell({ open: openProp, onOpenChange } = {}) {
  const [openLocal, setOpenLocal] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : openLocal;
  const setOpen = (v) => {
    if (!controlled) setOpenLocal(v);
    onOpenChange?.(v);
  };
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const res = await losApi.listNotifications(false);
      setItems(res.items || []);
      setUnread(res.unread_count || 0);
    } catch { /* ignore silently — likely 401 during logout */ }
  }, []);

  // Initial + polling
  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (pressedOutside(e, [containerRef, panelRef])) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = async () => {
    const nowOpen = !open;
    setOpen(nowOpen);
    if (nowOpen) { setLoading(true); await load(); setLoading(false); }
  };

  const openItem = async (n) => {
    if (!n.read) {
      try { await losApi.markNotificationRead(n.notification_id); } catch { /* ignore */ }
      setItems((prev) => prev.map(x => x.notification_id === n.notification_id ? { ...x, read: true } : x));
      setUnread((u) => Math.max(0, u - 1));
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const dismiss = async (n, e) => {
    e.stopPropagation();
    try { await losApi.dismissNotification(n.notification_id); } catch { /* ignore */ }
    setItems((prev) => prev.filter(x => x.notification_id !== n.notification_id));
    if (!n.read) setUnread((u) => Math.max(0, u - 1));
  };

  const markAll = async () => {
    try { await losApi.markAllNotificationsRead(); } catch { /* ignore */ }
    setItems((prev) => prev.map(x => ({ ...x, read: true })));
    setUnread(0);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={toggle}
        data-testid="notification-bell"
        title="Notifications"
        className="relative w-8 h-8 rounded-md flex items-center justify-center text-white/70 hover:text-white hover:bg-white/5 transition"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-bold flex items-center justify-center animate-in fade-in"
            style={{ background: '#6BA82E', color: '#030524', boxShadow: '0 0 8px rgba(107,168,46,0.7)' }}
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <TopNavPanel
          ref={panelRef}
          anchorRef={containerRef}
          onDismiss={() => setOpen(false)}
          stretchOnPhone
          gap={8}
          data-testid="notification-panel"
          className="sm:w-96 rounded-xl border shadow-2xl"
          style={{ background: '#ffffff', borderColor: 'rgba(7,11,95,0.15)', boxShadow: '0 12px 40px rgba(7,11,95,0.25)' }}
        >
          <div className="h-[3px]" style={{ background: 'linear-gradient(90deg, #6BA82E 0%, #8bc94f 60%, #6BA82E 100%)' }} />
          <div className="px-4 py-3 flex items-center justify-between border-b border-slate-100">
            <div>
              <div className="font-semibold text-[13px]" style={{ color: '#070B5F' }}>Notifications</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold">
                {unread > 0 ? `${unread} unread` : 'All caught up'}
              </div>
            </div>
            {unread > 0 && (
              <button
                onClick={markAll}
                data-testid="notification-mark-all-read"
                className="text-[11px] font-semibold inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-50"
                style={{ color: '#070B5F' }}
              >
                <CheckCheck size={12} /> Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[440px] overflow-y-auto">
            {loading && items.length === 0 && (
              <div className="p-8 text-center text-[12px] text-slate-400">Loading…</div>
            )}
            {!loading && items.length === 0 && (
              <div className="p-8 text-center">
                <Info size={22} className="mx-auto mb-2 text-slate-300" />
                <div className="text-[13px] font-semibold" style={{ color: '#070B5F' }}>No notifications yet</div>
                <div className="text-[11px] text-slate-500 mt-1">System events like AI Processor completions, PDFs, and disclosure signatures will show up here.</div>
              </div>
            )}
            {items.map((n) => {
              const Icon = ICON_MAP[n.icon] || Bell;
              return (
                <button
                  key={n.notification_id}
                  onClick={() => openItem(n)}
                  data-testid={`notification-item-${n.notification_id}`}
                  className="w-full flex items-start gap-3 px-4 py-3 border-b border-slate-100 hover:bg-slate-50 text-left group relative transition"
                  style={n.read ? {} : { background: 'rgba(107,168,46,0.04)' }}
                >
                  {!n.read && (
                    <span className="absolute left-1 top-4 w-1.5 h-1.5 rounded-full" style={{ background: '#6BA82E' }} />
                  )}
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(7,11,95,0.06)' }}>
                    <Icon size={14} style={{ color: '#070B5F' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold leading-tight" style={{ color: '#070B5F' }}>{n.title}</div>
                    {n.description && <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{n.description}</div>}
                    <div className="text-[10px] text-slate-400 mt-1 uppercase tracking-widest font-semibold">{timeAgo(n.created_at)} ago</div>
                  </div>
                  <button
                    onClick={(e) => dismiss(n, e)}
                    data-testid={`notification-dismiss-${n.notification_id}`}
                    className="opacity-0 group-hover:opacity-100 transition text-slate-300 hover:text-slate-600 p-0.5"
                    title="Dismiss"
                  >
                    <X size={12} />
                  </button>
                </button>
              );
            })}
          </div>
          <button
            onClick={() => { setOpen(false); navigate('/portal/notifications'); }}
            data-testid="notification-view-all"
            className="w-full px-4 py-2.5 text-[12px] font-semibold border-t border-slate-100 hover:bg-slate-50 transition"
            style={{ color: '#070B5F' }}
          >
            View all
          </button>
        </TopNavPanel>
      )}
    </div>
  );
}
